import React, { useMemo } from 'react';
import CustomModal from '../../../../components/mui/CustomModal';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress, Grid, TextField } from '@mui/material';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { CustomButton } from '../../../../components/mui/CustomButton';
import { useDispatch, useSelector } from 'react-redux';
import { saveOrUpdateTemplateFormDetailsRequest } from '../actions/settingsActions';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CustomTypography from '../../../../components/mui/CustomTypography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const LegalInfoUtahModal = ({ open, onClose }) => {
  const dispatch = useDispatch();

  const legalTemplateFormFieldDetails = useSelector((state) => state?.settings?.legalTemplateFormFieldDetails);
  const isLoadingSaveOrUpdateTemplateFormDetails = useSelector(
    (state) => state?.settings?.isLoadingSaveOrUpdateTemplateFormDetails,
  );
  const isLoadingLegalTemplateFormFieldDetails = useSelector(
    (state) => state?.settings?.isLoadingLegalTemplateFormFieldDetails,
  );

  const editorDetails = useSelector((state) => state?.editorDetails?.user?.data?.value);

  const editorDetailsData = useMemo(() => {
    return editorDetails && JSON.parse(editorDetails);
  }, [editorDetails]);

  const fieldsData = legalTemplateFormFieldDetails?.FormField?.Fields || [];
  const detailsData = legalTemplateFormFieldDetails?.FormField?.Details || [];
  const allowEdit = editorDetailsData?.Status !== 'Rework';

  const groupedFields = useMemo(() => {
    const grouped = {};

    detailsData.forEach(detail => {
      grouped[detail.Id] = {
        sectionName: detail.Name,
        fields: [],
      };
    });

    fieldsData.forEach(field => {
      const detailId = field.DetailId;
      if (grouped[detailId]) {
        grouped[detailId].fields.push(field);
      }
    });

    return grouped;
  }, [fieldsData, detailsData]);

  const initialValues = useMemo(() => {
    const values = {};
    const fv = legalTemplateFormFieldDetails?.FormDetail?.FieldValues || [];

    const valueById = {};
    fv.forEach(item => {
      if (item.FieldId) {
        valueById[item.FieldId.toLowerCase()] = item.FieldValue;
      }
    });

    fieldsData.forEach(field => {
      if (field.FieldType === 'Group' && Array.isArray(field.Children)) {
        const raw = valueById[field.FieldId?.toLowerCase()];
        field.Children.forEach(arrayField => {
          if (raw && typeof raw === 'object' && raw[arrayField.FieldName]) {
            values[arrayField.FieldName] = raw[arrayField.FieldName].map(item => {
              const obj = {};
              (arrayField.Children || []).forEach(child => {
                obj[child.FieldName] = item[child.FieldName] != null ? item[child.FieldName] : '';
              });
              return obj;
            });
          } else {
            values[arrayField.FieldName] = [
              (arrayField.Children || []).reduce((acc, child) => {
                acc[child.FieldName] = '';
                return acc;
              }, {})
            ];
          }
        });
      } else {
        values[field.FieldName] = valueById[field.FieldId?.toLowerCase()] ?? '';
      }
    });

    return values;
  }, [fieldsData, legalTemplateFormFieldDetails]);

  const generateValidationShape = (fields) => {
    const shape = {};
    fields.forEach(field => {
      if (field.FieldType === 'Group' && Array.isArray(field.Children)) {
        field.Children.forEach(arrayField => {
          shape[arrayField.FieldName] = Yup.array().of(
            Yup.object().shape(
              (arrayField.Children || []).reduce((subShape, child) => {
                let validator = Yup.string();
                if (child.Validation?.includes('required')) {
                  validator = validator.required(`${child.DisplayName} is required`);
                }
                subShape[child.FieldName] = validator;
                return subShape;
              }, {})
            )
          );
        });
      } else {
        let validator = Yup.string();
        if (field.Validation?.includes('required')) {
          validator = validator.required(`${field.DisplayName} is required`);
        }
        shape[field.FieldName] = validator;
      }
    });
    return shape;
  };

  const validationSchema = useMemo(() => {
    return Yup.object().shape(generateValidationShape(fieldsData));
  }, [fieldsData]);

  const onSubmit = (values) => {
    const submissionJson = [];

    fieldsData.forEach(field => {
      if (field.FieldType === 'Group' && Array.isArray(field.Children)) {
        const nestedObj = {};
        field.Children.forEach(arrayField => {
          const arr = values[arrayField.FieldName] || [];
          nestedObj[arrayField.FieldName] = arr.map(item => {
            const obj = {};
            (arrayField.Children || []).forEach(child => {
              obj[child.FieldName] = item[child.FieldName];
            });
            return obj;
          });
        });
        submissionJson.push({
          FieldId: field.FieldId,
          FieldValue: nestedObj,
        });
      } else {
        submissionJson.push({
          FieldId: field.FieldId,
          FieldValue: values[field.FieldName],
        });
      }
    });

    dispatch(
      saveOrUpdateTemplateFormDetailsRequest({
        id: legalTemplateFormFieldDetails.FormField.Id,
        legalTemplateId: legalTemplateFormFieldDetails.FormField.LegalTemplateId,
        audioFileId: editorDetailsData.Id,
        submissionJson,
        createdBy: editorDetailsData.UserId,
        isDeleted: false,
        legalTemplateName: editorDetailsData.TemplateTrans,
        cgaType: editorDetailsData.TemplateTrans === 5 ? editorDetailsData.CGAType : undefined,
      }),
    );
    onClose();
  };

  const renderField = (field, values, errors, touched, setFieldValue, setFieldTouched) => {
    if (field.FieldType === 'Group' && Array.isArray(field.Children)) {
      return <Grid item xs={12}>
        <div>
          <CustomTypography size="large" weight="medium" color="sub-primary">
            {field.DisplayName}
          </CustomTypography>
          {field.Children.map(arrayField => (
            <Grid item xs={12} key={arrayField.FieldName} style={{ marginTop: '8px', padding: '2px' }}>
              <Accordion defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <CustomTypography size="medium" weight="medium" color="sub-primary">
                    {arrayField.DisplayName}
                  </CustomTypography>
                </AccordionSummary>

                <AccordionDetails>
                  <FieldArray name={arrayField.FieldName}>
                    {({ push, remove }) => (
                      <>
                        {(values[arrayField.FieldName] || []).map((__, idx) => (
                          <Grid container spacing={2} key={idx} sx={{ marginTop: '2px' }}>
                            {(arrayField.Children || []).map(child => {
                              const fieldPath = `${arrayField.FieldName}[${idx}].${child.FieldName}`;
                              const fieldTouched = touched?.[arrayField.FieldName]?.[idx]?.[child.FieldName];
                              const fieldError = errors?.[arrayField.FieldName]?.[idx]?.[child.FieldName];
                              const fieldValue = values?.[arrayField.FieldName]?.[idx]?.[child.FieldName];
                              return (
                                <Grid item xs={12} md={6} key={child.FieldName}>
                                  <label className="form-label">
                                    {child.DisplayName}
                                    {child.Validation?.includes('required') && <span style={{ color: 'red' }}>*</span>}
                                  </label>

                                  <Field name={fieldPath}>
                                    {({ field: formikField, form }) => {
                                      if (child.FieldType === 'Date') {
                                        return (
                                          <>
                                            <DatePicker
                                              selected={fieldValue ? new Date(fieldValue) : null}
                                              onChange={(date) => {
                                                if (date) {
                                                  const mm = String(date.getMonth() + 1).padStart(2, '0');
                                                  const dd = String(date.getDate()).padStart(2, '0');
                                                  const yyyy = date.getFullYear();
                                                  form.setFieldValue(fieldPath, `${mm}/${dd}/${yyyy}`);
                                                } else {
                                                  form.setFieldValue(fieldPath, '');
                                                }
                                              }}
                                              onBlur={() => form.setFieldTouched(fieldPath, true)}
                                              onCalendarClose={() => form.setFieldTouched(fieldPath, true)}
                                              dateFormat="MM/dd/yyyy"
                                              placeholderText="MM/DD/YYYY"
                                              className={`mui-datepicker-input ${fieldTouched && fieldError ? 'custom-invalid' : ''}`}
                                            />
                                            {fieldTouched && fieldError && (
                                              <div
                                                style={{
                                                  color: '#d32f2f',
                                                  fontSize: '11px',
                                                  marginTop: '4px',
                                                  marginLeft: '12px',
                                                }}
                                              >
                                                {fieldError}
                                              </div>
                                            )}
                                          </>
                                        );
                                      }

                                      return (
                                        <TextField
                                          {...formikField}
                                          type={
                                            child.FieldType === 'Number'
                                              ? 'text'
                                              : child.FieldType === 'Time'
                                                ? 'time'
                                                : 'text'
                                          }
                                          size="small"
                                          fullWidth
                                          error={fieldTouched && Boolean(fieldError)}
                                          helperText={<ErrorMessage name={fieldPath} />}
                                          onChange={(e) => {
                                            const value = e.target.value;
                                            if (child.FieldType === 'Number') {
                                              const numericValue = value.replace(/[^0-9.]/g, '');
                                              form.setFieldValue(fieldPath, numericValue);
                                            } else {
                                              form.setFieldValue(fieldPath, value);
                                            }
                                          }}
                                        />
                                      );
                                    }}
                                  </Field>
                                </Grid>
                              )
                            }
                            )}
                            <Grid item xs={12} sx={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                              <CustomButton
                                type="button"
                                variant="secondary"
                                size="small"
                                sx={{
                                  backgroundColor: 'var(--primary)',
                                }}
                                onClick={() => remove(idx)}
                              >
                                -
                              </CustomButton>
                              {idx === (values[arrayField.FieldName] || []).length - 1 && <CustomButton
                                type="button"
                                variant="secondary"
                                size="small"
                                onClick={() => {
                                  const obj = (arrayField.Children || []).reduce((acc, c) => {
                                    acc[c.FieldName] = '';
                                    return acc;
                                  }, {});
                                  push(obj);
                                }}
                              >
                                +
                              </CustomButton>}
                            </Grid>
                          </Grid>
                        ))}
                        {values[arrayField.FieldName]?.length === 0 && (
                          <Grid container justifyContent="flex-start" sx={{ marginTop: '8px' }}>
                            <CustomButton
                              type="button"
                              variant="secondary"
                              size="small"
                              onClick={() => {
                                const obj = (arrayField.Children || []).reduce((acc, c) => {
                                  acc[c.FieldName] = '';
                                  return acc;
                                }, {});
                                push(obj);
                              }}
                            >
                              + Add
                            </CustomButton>
                          </Grid>
                        )}
                      </>
                    )}
                  </FieldArray>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </div>
      </Grid>
    }

    if (field.FieldType === 'Date') {
      return (
        <Grid item xs={12} md={6} key={field.FieldId}>
          <label className="form-label">
            {field.DisplayName}
            {field.Validation?.includes('required') && <span style={{ color: 'red' }}>*</span>}
          </label>
          <DatePicker
            selected={values[field.FieldName] ? new Date(values[field.FieldName]) : null}
            onChange={(date) => {
              if (date) {
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                const yyyy = date.getFullYear();
                setFieldValue(field.FieldName, `${mm}/${dd}/${yyyy}`);
              } else {
                setFieldValue(field.FieldName, '');
              }
            }}
            onBlur={() => setFieldTouched(field.FieldName, true)}
            onCalendarClose={() => setFieldTouched(field.FieldName, true)}
            dateFormat="MM/dd/yyyy"
            placeholderText="MM/DD/YYYY"
            className={`mui-datepicker-input ${touched[field.FieldName] && errors[field.FieldName] ? 'custom-invalid' : ''
              }`}
          />
          {touched[field.FieldName] && errors[field.FieldName] && (
            <div
              style={{
                color: '#d32f2f',
                fontSize: '11px',
                marginTop: '4px',
                marginLeft: '12px',
              }}
            >
              {errors[field.FieldName]}
            </div>
          )}
        </Grid>
      );
    }

    return (
      <Grid item xs={12} md={6} key={field.FieldId}>
        <label className="form-label">
          {field.DisplayName}
          {field.Validation?.includes('required') && <span style={{ color: 'red' }}>*</span>}
        </label>
        <Field name={field.FieldName}>
          {({ field: formikField, form }) => (
            <TextField
              {...formikField}
              type={
                field.FieldType === 'Number'
                  ? 'text'
                  : field.FieldType === 'Time'
                    ? 'time'
                    : 'text'
              }
              size="small"
              fullWidth
              error={touched[field.FieldName] && Boolean(errors[field.FieldName])}
              helperText={<ErrorMessage name={field.FieldName} />}
              onChange={(e) => {
                const value = e.target.value;
                if (field.FieldType === 'Number') {
                  const numericValue = value.replace(/[^0-9.]/g, '');
                  form.setFieldValue(field.FieldName, numericValue);
                } else {
                  form.setFieldValue(field.FieldName, value);
                }
              }}
            />
          )}
        </Field>
      </Grid>
    );
  };

  return (
    <CustomModal open={open} onClose={onClose} sx={{ maxWidth: '600px' }}>
      <div className="preview-dropdown-header">
        Legal Info Details
        <HighlightOffRoundedIcon
          onClick={onClose}
          style={{ cursor: 'pointer', opacity: 0.5, height: '28px', width: '28px' }}
        />
      </div>

      {isLoadingLegalTemplateFormFieldDetails ? (
        <div
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '20px' }}
        >
          <CircularProgress size={20} color="inherit" />
        </div>
      ) : (
        <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '0px 20px' }} className="hide-scrollbar">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({ errors, touched, values, setFieldValue, setFieldTouched }) => (
              <Form>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {legalTemplateFormFieldDetails?.FormField?.Details?.length > 0 ? (
                    Object.entries(groupedFields).map(([detailId, group]) => (
                      <React.Fragment key={detailId}>
                        <Grid item xs={12}>
                          <CustomTypography size="large" weight="medium" color="sub-primary">
                            {group.sectionName}
                          </CustomTypography>
                        </Grid>

                        {group.fields
                          .sort((a, b) => a.OrderNo - b.OrderNo)
                          .map((field) => (
                            renderField(field, values, errors, touched, setFieldValue, setFieldTouched)
                          ))}
                      </React.Fragment>
                    ))
                  ) : (
                    fieldsData
                      .sort((a, b) => a.OrderNo - b.OrderNo)
                      .map((field) =>
                        renderField(field, values, errors, touched, setFieldValue, setFieldTouched)
                      )
                  )}

                  <Grid item xs={12} sx={{ padding: '20px 0px' }}>
                    <CustomButton
                      type="submit"
                      variant="secondary"
                      disabled={isLoadingSaveOrUpdateTemplateFormDetails}
                      loading={isLoadingSaveOrUpdateTemplateFormDetails}
                    >
                      Save
                    </CustomButton>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </CustomModal>
  );
};

export default LegalInfoUtahModal;
